import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { userAPI, BulkCreateUsersData, BulkCreateUsersResult } from "../../services/userAPI";

interface BranchOption {
  id: string;
  name: string;
}

interface SalesPointOption {
  id: string;
  name: string;
  branchId: string;
}

interface BulkUserDialogProps {
  open: boolean;
  onClose: () => void;
  branches: BranchOption[];
  salesPoints: SalesPointOption[];
  onSuccess: (message: string) => void;
  /** Non-validation failures only (validation stays inside the dialog). */
  onError?: (message: string) => void;
}

const EMAIL_DOMAIN_RE = /^[a-zA-Z0-9][a-zA-Z0-9.-]*\.[a-zA-Z]{2,}$/;

const formatApiValidation = (validation: Record<string, unknown>): string =>
  Object.entries(validation)
    .map(([field, message]) => `${field}: ${message}`)
    .join("\n");

const BulkUserDialog: React.FC<BulkUserDialogProps> = ({
  open,
  onClose,
  branches,
  salesPoints,
  onSuccess,
  onError,
}) => {
  const queryClient = useQueryClient();
  const [branchId, setBranchId] = useState("");
  const [salesPointId, setSalesPointId] = useState("");
  const [role, setRole] = useState<"OPERATOR" | "MERCHANT">("OPERATOR");
  const [count, setCount] = useState(10);
  const [emailDomain, setEmailDomain] = useState("");
  const [startIndex, setStartIndex] = useState(1);
  const [result, setResult] = useState<BulkCreateUsersResult | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const filteredSalesPoints = useMemo(
    () => salesPoints.filter((d) => !branchId || d.branchId === branchId),
    [salesPoints, branchId]
  );

  useEffect(() => {
    if (!open) {
      setResult(null);
      setBranchId("");
      setSalesPointId("");
      setRole("OPERATOR");
      setCount(10);
      setEmailDomain("");
      setStartIndex(1);
      setFormError(null);
      setFieldErrors({});
    }
  }, [open]);

  const clearFieldError = (field: string) => {
    setFieldErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
    setFormError(null);
  };

  useEffect(() => {
    if (salesPointId && !filteredSalesPoints.some((d) => d.id === salesPointId)) {
      setSalesPointId("");
    }
  }, [branchId, salesPointId, filteredSalesPoints]);

  const previewEmails = useMemo(() => {
    const domain = emailDomain.trim().replace(/^@+/, "").toLowerCase();
    if (!domain) return [];
    const n = Math.min(Math.max(count, 1), 5);
    return Array.from({ length: n }, (_, i) => `user${startIndex + i}@${domain}`);
  }, [count, emailDomain, startIndex]);

  const { mutate, isPending } = useMutation({
    mutationFn: (payload: BulkCreateUsersData) => userAPI.bulkCreateUsers(payload),
    onSuccess: (data) => {
      setResult(data);
      queryClient.invalidateQueries({ queryKey: ["users"] });
      onSuccess(
        `Created ${data.summary.created} user(s)` +
          (data.summary.skipped ? `, skipped ${data.summary.skipped} existing` : "")
      );
    },
    onError: (err: any) => {
      const validation = err.response?.data?.validation;
      if (validation && typeof validation === "object") {
        const mapped: Record<string, string> = {};
        Object.entries(validation as Record<string, unknown>).forEach(([key, msg]) => {
          mapped[key] = String(msg);
        });
        setFieldErrors(mapped);
        setFormError(
          formatApiValidation(validation as Record<string, unknown>) ||
            err.response?.data?.message ||
            "Validation failed"
        );
        return;
      }
      const message = err.response?.data?.message || "Bulk create failed";
      setFormError(message);
      setFieldErrors({});
      onError?.(message);
    },
  });

  const handleSubmit = () => {
    setFormError(null);
    setFieldErrors({});

    if (!salesPointId || !emailDomain.trim()) {
      const missing: Record<string, string> = {};
      if (!salesPointId) missing.salesPointId = "SalesPoint is required";
      if (!emailDomain.trim()) missing.emailDomain = "Email domain is required";
      setFieldErrors(missing);
      setFormError("Please fill in all required fields.");
      return;
    }

    const domain = emailDomain.trim().replace(/^@+/, "").toLowerCase();
    if (!EMAIL_DOMAIN_RE.test(domain)) {
      setFieldErrors({
        emailDomain: "Enter a valid domain (e.g. mycompany.com)",
      });
      setFormError("emailDomain: Enter a valid domain (e.g. mycompany.com)");
      return;
    }

    mutate({
      branchId: branchId || undefined,
      salesPointId,
      count,
      role,
      emailDomain: domain,
      startIndex,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Bulk Add Users</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Creates users as <strong>user1@domain</strong>, <strong>user2@domain</strong>, etc. Existing
          emails are skipped. Each Operator/Merchant gets a random 6-digit MPIN (login password).
        </Typography>

        {formError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setFormError(null)}>
            {formError.split("\n").map((line, index) => (
              <Box key={index} component="div">
                {line}
              </Box>
            ))}
          </Alert>
        )}

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth error={!!fieldErrors.branchId}>
              <InputLabel>Branch (Optional)</InputLabel>
              <Select
                value={branchId}
                label="Branch (Optional)"
                onChange={(e) => {
                  setBranchId(e.target.value);
                  clearFieldError("branchId");
                }}
              >
                <MenuItem value="">
                  <em>All / None (Optional)</em>
                </MenuItem>
                {branches.map((b) => (
                  <MenuItem key={b.id} value={b.id}>
                    {b.name}
                  </MenuItem>
                ))}
              </Select>
              {fieldErrors.branchId && (
                <FormHelperText>{fieldErrors.branchId}</FormHelperText>
              )}
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl
              fullWidth
              required
              error={!!fieldErrors.salesPointId}
            >
              <InputLabel>SalesPoint</InputLabel>
              <Select
                value={salesPointId}
                label="Sales point"
                onChange={(e) => {
                  setSalesPointId(e.target.value);
                  clearFieldError("salesPointId");
                }}
              >
                <MenuItem value="">
                  <em>Select salesPoint</em>
                </MenuItem>
                {filteredSalesPoints.map((d) => (
                  <MenuItem key={d.id} value={d.id}>
                    {d.name}
                  </MenuItem>
                ))}
              </Select>
              {fieldErrors.salesPointId && (
                <FormHelperText>{fieldErrors.salesPointId}</FormHelperText>
              )}
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Role</InputLabel>
              <Select value={role} label="Role" onChange={(e) => setRole(e.target.value as typeof role)}>
                <MenuItem value="OPERATOR">Operator</MenuItem>
                <MenuItem value="MERCHANT">Merchant</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              label="Email domain"
              placeholder="mycompany.com"
              value={emailDomain}
              onChange={(e) => {
                setEmailDomain(e.target.value);
                clearFieldError("emailDomain");
              }}
              error={!!fieldErrors.emailDomain}
              helperText={
                fieldErrors.emailDomain || "Domain only (e.g. mycompany.com) — not a full email"
              }
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              type="number"
              label="How many users to create"
              inputProps={{ min: 1, max: 100 }}
              value={count}
              onChange={(e) => {
                setCount(Math.min(100, Math.max(1, Number(e.target.value) || 1)));
                clearFieldError("count");
              }}
              error={!!fieldErrors.count}
              helperText={fieldErrors.count}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="number"
              label="Start index"
              inputProps={{ min: 1, max: 9999 }}
              value={startIndex}
              onChange={(e) => {
                setStartIndex(Math.max(1, Number(e.target.value) || 1));
                clearFieldError("startIndex");
              }}
              error={!!fieldErrors.startIndex}
              helperText={
                fieldErrors.startIndex || "First user number (default 1 → user1@…)"
              }
            />
          </Grid>
        </Grid>

        {previewEmails.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
              Preview
            </Typography>
            <Typography variant="body2">{previewEmails.join(", ")}…</Typography>
          </Box>
        )}

        {result && result.created.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Alert severity="success" sx={{ mb: 2 }}>
              Created {result.summary.created} user(s)
              {result.summary.skipped > 0
                ? ` · Skipped ${result.summary.skipped} existing email(s)`
                : ""}
            </Alert>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Email</TableCell>
                  <TableCell>MPIN</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {result.created.map((u) => (
                  <TableRow key={u.id}>
                    <TableCell>{u.email}</TableCell>
                    <TableCell>
                      <Typography fontWeight={600} fontFamily="monospace">
                        {u.mpin || "—"}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} color="inherit">
          {result ? "Close" : "Cancel"}
        </Button>
        {!result && (
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={isPending || !branchId || !salesPointId || !emailDomain.trim()}
          >
            {isPending ? <CircularProgress size={22} color="inherit" /> : "Create users"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default BulkUserDialog;
